import React from 'react';
import { Shield, CheckCircle2, Building2, Award, Crown, User as UserIcon, ShieldOff } from 'lucide-react';

/**
 * TrustLevelBadge — 5-level trust framework visual
 * 0: Unverified (gray)
 * 1: Identity Verified (blue)
 * 2: Business Verified (green)
 * 3: Premium Verified (amber/gold)
 * 4: Enterprise (black)
 */
const LEVELS = {
  0: { label: 'Unverified', short: 'L0', icon: ShieldOff,
       bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-400' },
  1: { label: 'Identity Verified', short: 'L1 · Identity', icon: UserIcon,
       bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200', dot: 'bg-blue-500' },
  2: { label: 'Business Verified', short: 'L2 · Business', icon: Building2,
       bg: 'bg-[#0055FF]/10', text: 'text-[#0044CC]', border: 'border-[#0055FF]/30', dot: 'bg-[#0055FF]' },
  3: { label: 'Premium Verified', short: 'L3 · Premium', icon: Award,
       bg: 'bg-[#FDF6E3]', text: 'text-[#8B6914]', border: 'border-[#D4AF37]', dot: 'bg-[#D4AF37]' },
  4: { label: 'Enterprise', short: 'L4 · Enterprise', icon: Crown,
       bg: 'bg-gray-900', text: 'text-white', border: 'border-gray-900', dot: 'bg-white' },
};

export const TrustLevelBadge = ({ level = 0, size = 'md', showLabel = true }) => {
  const cfg = LEVELS[level] || LEVELS[0];
  const Icon = cfg.icon;
  const sizes = size === 'sm' ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  return (
    <span
      className={`inline-flex items-center border rounded font-medium ${sizes} ${cfg.bg} ${cfg.text} ${cfg.border}`}
      data-testid={`trust-level-badge-${level}`}
      title={cfg.label}
    >
      <Icon className={iconSize} strokeWidth={2.5} />
      {showLabel && (size === 'sm' ? cfg.short : cfg.label)}
    </span>
  );
};

export const TrustScoreCard = ({ score = 30, level = 0 }) => {
  const cfg = LEVELS[level] || LEVELS[0];
  // Palette: blue for high, blue for mid, grey for low, gold ONLY for premium level
  const scoreColor =
    level >= 3 ? 'text-[#8B6914]'
    : score >= 80 ? 'text-[#0044CC]'
    : score >= 60 ? 'text-[#0055FF]'
    : score >= 40 ? 'text-gray-800'
    : 'text-gray-500';
  const barColor =
    level >= 3 ? 'bg-[#D4AF37]'
    : score >= 60 ? 'bg-[#0055FF]'
    : 'bg-gray-400';
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="trust-score-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-gray-500">Trust Score</span>
        <TrustLevelBadge level={level} size="sm" />
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`font-mono text-3xl font-bold ${scoreColor}`}>{score}</span>
        <span className="text-sm text-gray-400">/100</span>
      </div>
      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

export const VerificationChip = ({ verified, label, icon: Icon = CheckCircle2 }) => (
  <span
    className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded border ${
      verified ? 'bg-blue-50 text-[#0044CC] border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'
    }`}
  >
    <Icon className="w-3 h-3" strokeWidth={2.5} />
    {label}
  </span>
);

export default TrustLevelBadge;
export { LEVELS };
